import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { StyledGallery } from "./Gallery.styled";
import { ArtItemComponent } from "../../Components/ArtItem/ArtItem";
import { useEffect, useState } from "react";
import { useArt } from "../../stores/useArt";
import { Art } from "../../Models/Art";
import { DarkOverlay } from "../../darkOverlay/DarkOverlay";
import { GalleryArtMenu } from "../../Components/GalleryArtMenu/GalleryArtMenu";

type FilterValue =
  | "nieuw-oud"
  | "oud-nieuw"
  | "schilderijen"
  | "sculpturen"
  | "meest-bekeken"
  | "a-z";

export const Gallery = () => {
  const [filter, setFilter] = useState<FilterValue | null>("nieuw-oud");
  const [darkOverlayOpened, setDarkOverlayOpened] = useState<boolean>(false);
  const [selectedArtItem, setSelectedArtItem] = useState<Art | undefined>(undefined);
  const [art, setArt] = useState<Art[]>([]);
  const [filteredArtList, setFilteredArtList] = useState<Art[]>([]);
  const { fetchAllArt } = useArt();

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: FilterValue | null
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const updateItems = async () => {
    const res = await fetchAllArt();
    if (res) setArt(res);
  };

  const handleSelect = (artItem: Art) => {
    setSelectedArtItem(artItem);
    setDarkOverlayOpened(true);
  }

  useEffect(() => {
    updateItems();
    // eslint-disable-next-line 
  }, []);

  useEffect(() => {
    if (!art) return;

    let filtered = [...art];

    switch (filter) {
      case "nieuw-oud":
        filtered.sort(
          (a, b) =>
            new Date(b.creationDate).getTime() -
            new Date(a.creationDate).getTime()
        );
        break;

      case "oud-nieuw":
        filtered.sort(
          (a, b) =>
            new Date(a.creationDate).getTime() -
            new Date(b.creationDate).getTime()
        );
        break;

      case "schilderijen":
        filtered = filtered.filter((a) => a.type === "PAINTING");
        break;

      case "sculpturen":
        filtered = filtered.filter((a) => a.type === "SCULPTURE");
        break;

      case "meest-bekeken":
        filtered.sort((a, b) => b.viewCount - a.viewCount);
        break;

      case "a-z":
        filtered.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
        );
        break;

      default:
        filtered = [...art];
    }

    setFilteredArtList(filtered);
  }, [filter, art]);

  return (
    <StyledGallery>
      <DarkOverlay
        opened={darkOverlayOpened}
        component={selectedArtItem && <GalleryArtMenu artItem={selectedArtItem} />}
        onClose={() => {
          setDarkOverlayOpened(false);
        }}
      />
      <Box className="header">
        <Typography variant="h1">Kunstwerken</Typography>
      </Box>
      <Box className="content">
        <Box className="filterBar">
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleChange}
            aria-label="filter" className="filters">
            <ToggleButton value="nieuw-oud" className="filter">Nieuw - Oud</ToggleButton>
            <ToggleButton value="oud-nieuw" className="filter">Oud - Nieuw</ToggleButton>
            <ToggleButton value="schilderijen" className="filter">Schilderijen</ToggleButton>
            <ToggleButton value="sculpturen" className="filter">Sculpturen</ToggleButton>
            <ToggleButton value="meest-bekeken" className="filter">Meest Bekeken</ToggleButton>
            <ToggleButton value="a-z" className="filter">A-Z</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box className="gallery">
          {filteredArtList.map((artItem) => (
            <ArtItemComponent artItem={artItem} onClick={(artItem) => handleSelect(artItem)}/>
          ))}
        </Box>
      </Box>
    </StyledGallery>
  );
};
